import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../components/common/modal/Modal";
import Input from "../../components/common/input/Input";
import SwitchButton from "../../components/common/switch-button/SwitchButton";
import AttributesService from "../../api/services/attributes.service";
import styles from "./AttributesModal.module.scss";
import Plus from "../../assets/images/svgs/Plus";
import Minus from "../../assets/images/svgs/Minus";

interface AttributesModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryId: number | null;
  categoryName: string;
  editAttributeId?: number | null;
  onSuccess?: () => void;
}

type Language = "en" | "az" | "es" | "de";

interface Errors {
  name_en: string;
}

const AttributesModal = ({
  isOpen,
  onClose,
  categoryId,
  categoryName,
  editAttributeId = null,
  onSuccess,
}: AttributesModalProps) => {
  const { t } = useTranslation("translation");
  const [activeLanguage, setActiveLanguage] = useState<Language>("en");
  const [formData, setFormData] = useState({
    name_en: "",
    name_az: "",
    name_es: "",
    name_de: "",
    is_filter: false,
    is_view: false,
    category_id: null as number | null,
    options_api: "",
    values_en: [{ name: "" }],
    values_az: [{ name: "" }],
    values_es: [{ name: "" }],
    values_de: [{ name: "" }],
  });
  const [errors, setErrors] = useState<Partial<Errors>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);

  const languages = [
    { code: "en" as Language, label: "English", flag: "🇬🇧" },
    { code: "az" as Language, label: "Azərbaycanca", flag: "🇦🇿" },
    { code: "es" as Language, label: "Español", flag: "🇪🇸" },
    { code: "de" as Language, label: "Deutsch", flag: "🇩🇪" },
  ];

  // Helper function to parse values from backend
  const parseValues = (values: any) => {
    if (!values) return [{ name: "" }];

    if (typeof values === "string") {
      try {
        const parsed = JSON.parse(values);
        return Array.isArray(parsed) && parsed.length > 0
          ? parsed
          : [{ name: "" }];
      } catch {
        return [{ name: "" }];
      }
    }

    if (Array.isArray(values)) {
      return values.length > 0 ? values : [{ name: "" }];
    }

    return [{ name: "" }];
  };

  // Load attribute data for editing
  useEffect(() => {
    const loadAttributeData = async () => {
      if (isOpen && editAttributeId) {
        setIsLoadingEdit(true);
        try {
          const res = await AttributesService.getById(editAttributeId);
          const data = res.data;

          setFormData({
            name_en: data.name_en || data.name || "",
            name_az: data.name_az || "",
            name_es: data.name_es || "",
            name_de: data.name_de || "",
            category_id: data.category_id,
            options_api: data.options_api || "",
            values_en: parseValues(data.values_en),
            values_az: parseValues(data.values_az),
            values_es: parseValues(data.values_es),
            values_de: parseValues(data.values_de),
            is_filter: data.is_filter,
            is_view: data.is_view,
          });
        } catch (error) {
          console.error("Error loading attribute:", error);
        } finally {
          setIsLoadingEdit(false);
        }
      }
    };

    loadAttributeData();
  }, [isOpen, editAttributeId]);

  // Pre-populate category when modal opens for new attribute
  useEffect(() => {
    if (isOpen && categoryId && !editAttributeId) {
      setFormData((prev) => ({ ...prev, category_id: categoryId }));
    }
  }, [isOpen, categoryId, editAttributeId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name_en: "",
        name_az: "",
        name_es: "",
        name_de: "",
        is_filter: false,
        is_view: false,
        category_id: null,
        options_api: "",
        values_en: [{ name: "" }],
        values_az: [{ name: "" }],
        values_es: [{ name: "" }],
        values_de: [{ name: "" }],
      });
      setErrors({});
      setActiveLanguage("en");
      setIsSubmitting(false);
      setIsLoadingEdit(false);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Errors> = {};
    if (!formData.name_en?.trim()) {
      newErrors.name_en = t(
        "attributes_modal.errors.english_name_required",
      ) as string;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const filterValues = (values: { id?: number; name: string }[]) => {
    return values
      .filter((v) => v.name.trim() !== "")
      .map((v) => ({ name: v.name.trim() }));
  };

  const handleSubmit = async () => {
    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const data = new FormData();

      const cleanValuesEn = filterValues(formData.values_en);
      const cleanValuesAz = filterValues(formData.values_az);
      const cleanValuesEs = filterValues(formData.values_es);
      const cleanValuesDe = filterValues(formData.values_de);

      data.append("name_en", formData.name_en);
      if (formData.name_az) data.append("name_az", formData.name_az);
      if (formData.name_es) data.append("name_es", formData.name_es);
      if (formData.name_de) data.append("name_de", formData.name_de);
      data.append("is_filter", String(formData.is_filter));
      data.append("is_view", String(formData.is_view));
      data.append("category_id", String(formData.category_id));
      data.append("type", "2");

      if (formData.options_api?.trim()) {
        data.append("options_api", formData.options_api);
      }

      if (cleanValuesEn.length > 0) {
        data.append("values_en", JSON.stringify(cleanValuesEn));
      }
      if (cleanValuesAz.length > 0) {
        data.append("values_az", JSON.stringify(cleanValuesAz));
      }
      if (cleanValuesEs.length > 0) {
        data.append("values_es", JSON.stringify(cleanValuesEs));
      }
      if (cleanValuesDe.length > 0) {
        data.append("values_de", JSON.stringify(cleanValuesDe));
      }

      if (editAttributeId) {
        await AttributesService.updateData(editAttributeId, data);
      } else {
        await AttributesService.createOne(data);
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error("Error saving attribute:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentValues = () => {
    switch (activeLanguage) {
      case "az":
        return formData.values_az;
      case "es":
        return formData.values_es;
      case "de":
        return formData.values_de;
      default:
        return formData.values_en;
    }
  };

  const updateCurrentValues = (newValues: { id?: number; name: string }[]) => {
    switch (activeLanguage) {
      case "az":
        setFormData({ ...formData, values_az: newValues });
        break;
      case "es":
        setFormData({ ...formData, values_es: newValues });
        break;
      case "de":
        setFormData({ ...formData, values_de: newValues });
        break;
      default:
        setFormData({ ...formData, values_en: newValues });
    }
  };

  const renderLanguageTabs = () => (
    <div className={styles.languageTabs}>
      {languages.map((lang) => (
        <button
          key={lang.code}
          type="button"
          className={`${styles.languageTab} ${
            activeLanguage === lang.code ? styles.active : ""
          }`}
          onClick={() => setActiveLanguage(lang.code)}
        >
          <span className={styles.flag}>{lang.flag}</span>
          <span className={styles.langLabel}>{lang.label}</span>
          {lang.code === "en" && <span className={styles.required}>*</span>}
        </button>
      ))}
    </div>
  );

  if (isLoadingEdit) {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={t("attributes_modal.loading") as string}
        onSubmit={() => {}}
      >
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>{t("attributes_modal.loading_data") as string}</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        editAttributeId
          ? `${t("attributes_modal.edit_title") as string} "${categoryName}"`
          : `${t("attributes_modal.add_title") as string} "${categoryName}"`
      }
      onSubmit={handleSubmit}
    >
      <form className={styles.form}>
        {renderLanguageTabs()}

        <div className={styles.languageContent}>
          {activeLanguage === "en" && (
            <Input
              label={t("attributes_modal.name_english") as string}
              placeholder={
                t("attributes_modal.name_english_placeholder") as string
              }
              value={formData.name_en}
              onChange={(e) => {
                setFormData({ ...formData, name_en: e.target.value });
                setErrors((prev) => ({ ...prev, name_en: "" }));
              }}
              error={errors.name_en}
            />
          )}

          {activeLanguage === "az" && (
            <Input
              label={t("attributes_modal.name_azerbaijani") as string}
              placeholder={
                t("attributes_modal.name_azerbaijani_placeholder") as string
              }
              value={formData.name_az}
              onChange={(e) => {
                setFormData({ ...formData, name_az: e.target.value });
              }}
            />
          )}

          {activeLanguage === "es" && (
            <Input
              label={t("attributes_modal.name_spanish") as string}
              placeholder={
                t("attributes_modal.name_spanish_placeholder") as string
              }
              value={formData.name_es}
              onChange={(e) => {
                setFormData({ ...formData, name_es: e.target.value });
              }}
            />
          )}

          {activeLanguage === "de" && (
            <Input
              label={t("attributes_modal.name_german") as string}
              placeholder={
                t("attributes_modal.name_german_placeholder") as string
              }
              value={formData.name_de}
              onChange={(e) => {
                setFormData({ ...formData, name_de: e.target.value });
              }}
            />
          )}
        </div>

        <div className={styles.api_section}>
          <Input
            label={t("attributes_modal.options_api") as string}
            placeholder={
              t("attributes_modal.options_api_placeholder") as string
            }
            value={formData.options_api}
            onChange={(e) => {
              setFormData({ ...formData, options_api: e.target.value });
            }}
          />
        </div>

        {!formData.options_api && (
          <div className={styles.valuesBlock}>
            <div className={styles.values_header}>
              <label className={styles.label}>
                {t("attributes_modal.values_label") as string} (
                {languages.find((l) => l.code === activeLanguage)?.label})
              </label>
            </div>

            <div className={styles.values_container}>
              {getCurrentValues().map((val, index) => (
                <div key={index} className={styles.valueRow}>
                  <Input
                    placeholder={`${t("attributes_modal.value_placeholder") as string} ${index + 1}`}
                    value={val.name}
                    onChange={(e) => {
                      const updatedValues = [...getCurrentValues()];
                      updatedValues[index].name = e.target.value;
                      updateCurrentValues(updatedValues);
                    }}
                  />
                  <div className={styles.valueActions}>
                    {index === getCurrentValues().length - 1 && (
                      <button
                        type="button"
                        onClick={() =>
                          updateCurrentValues([
                            ...getCurrentValues(),
                            { name: "" },
                          ])
                        }
                        className={`${styles.actionButton} ${styles.add_button}`}
                        title={t("attributes_modal.add_value") as string}
                      >
                        <Plus />
                      </button>
                    )}
                    {getCurrentValues().length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const updatedValues = getCurrentValues().filter(
                            (_, i) => i !== index,
                          );
                          updateCurrentValues(updatedValues);
                        }}
                        className={`${styles.actionButton} ${styles.remove_button}`}
                        title={t("attributes_modal.remove_value") as string}
                      >
                        <Minus />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={styles.settings_section}>
          <div className={styles.section_header}></div>
          <div className={styles.switch_btns}>
            <div className={styles.switch_item}>
              <SwitchButton
                label={t("attributes_modal.show_in_filters") as string}
                checked={formData.is_filter}
                onChange={(value) => {
                  setFormData({ ...formData, is_filter: value });
                }}
              />
            </div>
            <div className={styles.switch_item}>
              <SwitchButton
                label={t("attributes_modal.user_changeable") as string}
                checked={formData.is_view}
                onChange={(value) => {
                  setFormData({ ...formData, is_view: value });
                }}
              />
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default AttributesModal;
